import { Dayjs } from 'dayjs';
import { isNotNullOrUndefined } from './null-safety.js';
import { LocalizedString } from '../models/serializable/localized-string.js';
import { Currency, getCurrencyOfCountry } from '../models/serializable/currency.js';
import { TicketTemplate, TicketTemplateChannel, TicketTemplateStatus } from '../models/serializable/ticket-template/index.js';
import { ComponentList } from '../components/core/component-list.js';
import { PredefinedTemplateComponent } from '../components/ticket-template/predefined-template-component.js';
import { DateSpan } from '../models/serializable/date-span.js';
import { Price } from '../models/serializable/price.js';
import { Cardinal } from '../models/core/cardinal.js';
import { LocalizedRichDescription } from '../models/serializable/localized-rich-description.js';
import { Language } from '../models/serializable/language.js';
import { EventProfile } from '../models/serializable/event-profile.js';
import { TicketTemplateComponent } from '../components/events/ticket-template-component.js';
import { generateUniqueDatabaseId } from '../models/dto/data-model-utils.js';

type TicketTemplateDefinitions = {
  protocol: {
    AAA: TicketTemplateDefinition,
    STAFF: TicketTemplateDefinition,
    CREW: TicketTemplateDefinition,
    ARTIST: TicketTemplateDefinition,
    STAGE: TicketTemplateDefinition,
    BACKSTAGE: TicketTemplateDefinition,
    GUEST: TicketTemplateDefinition,
    PROTOCOL: TicketTemplateDefinition,
    PROTOCOL_VIP: TicketTemplateDefinition,
    PROTOCOL_GOLD_VIP: TicketTemplateDefinition,
  },
};

type UniqueId = { uniqueId: string };
type Names = { names: LocalizedString };
type Now = { now: Dayjs };
type CurrencyArg = { currency: Currency };
type Event = { event: EventProfile };

type TicketTemplateDefinitionContructorArgs = UniqueId & Names;
type TicketTemplateArgs = Now & CurrencyArg;
type AddPredefinedProtocolTicketsToEventArgs = Event & Now;

class TicketTemplateDefinition {
  private constructor(
    public readonly uniqueId: string,
    public readonly names: LocalizedString,
    public readonly channel: TicketTemplateChannel,
  ) {}

  static admin({ uniqueId, names }: TicketTemplateDefinitionContructorArgs): TicketTemplateDefinition {
    return new TicketTemplateDefinition(uniqueId, names, TicketTemplateChannel.Admin);
  }

  static online({ uniqueId, names }: TicketTemplateDefinitionContructorArgs): TicketTemplateDefinition {
    return new TicketTemplateDefinition(uniqueId, names, TicketTemplateChannel.Online);
  }

  static promoter({ uniqueId, names }: TicketTemplateDefinitionContructorArgs): TicketTemplateDefinition {
    return new TicketTemplateDefinition(uniqueId, names, TicketTemplateChannel.Promoter);
  }

  static protocol({ uniqueId, names }: TicketTemplateDefinitionContructorArgs): TicketTemplateDefinition {
    return new TicketTemplateDefinition(uniqueId, names, TicketTemplateChannel.Protocol);
  }

  toTicketTemplate({ now, currency }: TicketTemplateArgs): TicketTemplate {
    return TicketTemplate.withChannel({
      applicationFee: null,
      components: new ComponentList([
        PredefinedTemplateComponent.create({ uniqueId: this.uniqueId }),
      ]),
      dateSpan: new DateSpan(now, now),
      isLimited: false,
      isPurchasableBySelf: false,
      names: this.names.clone(),
      sideEffects: [],
      siblingTicketTemplateIds: [],
      originalPrice: null,
      price: Price.withCurrencyFromAmount(currency, 0),
      quantity: Cardinal.infinity,
      minQuantity: Cardinal.finite(1),
      overrideVat: null,
      richDescriptions: LocalizedRichDescription.empty(),
      scanDateSpan: null,
      specialOffer: null,
      status: TicketTemplateStatus.Live,
      uniqueId: generateUniqueDatabaseId(),
      availabilityModifier: null,
      ticketSupplementTemplateId: null,
      channel: this.channel,
    });
  }
}

export abstract class PredefinedTicketTemplateUtils {
  private static definitions: TicketTemplateDefinitions = {
    protocol: {
      AAA: TicketTemplateDefinition.protocol({
        uniqueId: 'AAA',
        names: LocalizedString.localized(new Map([
          [Language.English, 'AAA'],
        ])),
      }),
      STAFF: TicketTemplateDefinition.protocol({
        uniqueId: 'STAFF',
        names: LocalizedString.localized(new Map([
          [Language.English, 'Staff'],
          [Language.Romanian, 'Staff'],
        ])),
      }),
      CREW: TicketTemplateDefinition.protocol({
        uniqueId: 'CREW',
        names: LocalizedString.localized(new Map([
          [Language.English, 'Crew'],
          [Language.Romanian, 'Echipă'],
        ])),
      }),
      ARTIST: TicketTemplateDefinition.protocol({
        uniqueId: 'ARTIST',
        names: LocalizedString.localized(new Map([
          [Language.English, 'Artist'],
          [Language.Romanian, 'Artist'],
        ])),
      }),
      STAGE: TicketTemplateDefinition.protocol({
        uniqueId: 'STAGE',
        names: LocalizedString.localized(new Map([
          [Language.English, 'Stage'],
          [Language.Romanian, 'Scenă'],
        ])),
      }),
      BACKSTAGE: TicketTemplateDefinition.protocol({
        uniqueId: 'BACKSTAGE',
        names: LocalizedString.localized(new Map([
          [Language.English, 'Backstage'],
          [Language.Romanian, 'Backstage'],
        ])),
      }),
      GUEST: TicketTemplateDefinition.protocol({
        uniqueId: 'GUEST',
        names: LocalizedString.localized(new Map([
          [Language.English, 'Guest'],
          [Language.Romanian, 'Oaspete'],
        ])),
      }),
      PROTOCOL: TicketTemplateDefinition.protocol({
        uniqueId: 'PROTOCOL',
        names: LocalizedString.localized(new Map([
          [Language.English, 'Guest'],
          [Language.Romanian, 'Protocol'],
          [Language.Hungarian, 'Tiszteletjegy'],
        ])),
      }),
      PROTOCOL_VIP: TicketTemplateDefinition.protocol({
        uniqueId: 'PROTOCOL_VIP',
        names: LocalizedString.localized(new Map([
          [Language.English, 'Guest VIP'],
          [Language.Romanian, 'Protocol VIP'],
          [Language.Hungarian, 'Tiszteletjegy VIP'],
        ])),
      }),
      PROTOCOL_GOLD_VIP: TicketTemplateDefinition.protocol({
        uniqueId: 'PROTOCOL_GOLD_VIP',
        names: LocalizedString.localized(new Map([
          [Language.English, 'Guest Gold VIP'],
          [Language.Romanian, 'Protocol Gold VIP'],
          [Language.Hungarian, 'Tiszteletjegy Gold VIP'],
        ])),
      }),
    },
  };

  static addPredefinedProtocolTicketsToEvent({ event, now } : AddPredefinedProtocolTicketsToEventArgs): EventProfile {
    function findPredefinedTemplateId(ticket: TicketTemplate): string | null {
      const component = ticket.findComponentByTypeId<PredefinedTemplateComponent>(PredefinedTemplateComponent.TYPE_ID);
      return component?.uniqueId ?? null;
    }

    const component = event.findComponentByTypeId<TicketTemplateComponent>(TicketTemplateComponent.TYPE_ID);
    const newComponent = component?.clone() ?? TicketTemplateComponent.empty();

    const predefinedTicketsAlreadyPresent = new Set(newComponent.tickets
        .map(findPredefinedTemplateId)
        .filter(isNotNullOrUndefined));

    for(const predefinedTicket of Object.values(this.definitions.protocol)) {
      if(predefinedTicketsAlreadyPresent.has(predefinedTicket.uniqueId)) {
        continue;
      }

      const ticket = predefinedTicket.toTicketTemplate({
        now: now,
        currency: getCurrencyOfCountry(event.country),
      });

      newComponent.tickets.push(ticket);
      predefinedTicketsAlreadyPresent.add(predefinedTicket.uniqueId);
    }

    const updatedEvent = event.clone();
    updatedEvent.addOrReplaceComponent(newComponent);

    return updatedEvent;
  }

  static providePredefinedDefaultTicketTemplates({ now, currency }: TicketTemplateArgs) : TicketTemplate[] {
    return [this.definitions.protocol.PROTOCOL.toTicketTemplate({
      now,
      currency,
    })];
  }
}
